import React, { useState } from 'react';
import { Copy, CheckCircle2, Users, ArrowRightLeft, Mail } from 'lucide-react';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { DjayNarrator } from '../../../mascot';
import { useAppStore } from '../../../app/hooks/useAppStore';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
interface InviteCoTeacherPanelProps {
  open: boolean;
  onClose: () => void;
  classroom: Classroom;
  activeAccount: TeacherAccount;
  /** All teachers known to the active account, used to render names of existing co-teachers. */
  teacherById: Record<string, TeacherAccount | undefined>;
  /** Owner-only: open the transfer-ownership confirmation. */
  onRequestTransfer?: (newOwnerId: string) => void;
}
/**
 * Owner-facing panel to invite, list, and manage co-teachers for a classroom.
 *
 * ─── DIT DEELT NIET LANGER DE KLASCODE (P0, SHORT_CODE_SECURITY.md B-1) ───
 *
 * Hier stond de `joinCode` van de klas. Diezelfde code krijgen dertig
 * leerlingen om lid te worden; hem ook laten gelden als docentcode betekende
 * dat elk kind iemand co-docent kon maken, met inzage in álle leerlingen van de
 * klas. Dat is rechtenescalatie, niet een zwak geheim.
 *
 * Nu een eigen `coTeacherCode` van 8 tekens, die alleen de eigenaar ziet en die
 * bestaat zodra hij hem aanmaakt. Geen code = niemand kan co-docent worden, en
 * dat is de juiste faalstand.
 *
 * Djay-zone: small bubble on empty co-teacher list (Zone 3).
 */
export function InviteCoTeacherPanel({
  open,
  onClose,
  classroom,
  activeAccount,
  teacherById,
  onRequestTransfer
}: InviteCoTeacherPanelProps) {
  const {
    regenerateCoTeacherCode
  } = useAppStore();
  const [copied, setCopied] = useState(false);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [pendingReplace, setPendingReplace] = useState(false);
  const coTeachers = classroom.coTeacherIds ?? [];
  const canAddMore = coTeachers.length < MAX_CO_TEACHERS_PER_CLASSROOM;
  const isOwner = classroom.teacherId === activeAccount.id;
  const code = classroom.coTeacherCode;
  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleCreateOrReplace = () => {
    regenerateCoTeacherCode(activeAccount.id, classroom.id);
    setPendingReplace(false);
  };
  return <>
      <SlideOver open={open} onClose={onClose} title="Co-docent uitnodigen" description={`Voor klas: ${classroom.name}`} width="md" data-id="element-9848" data-mp="1jp3ucsh4mktc">
        
        <div className="p-6 space-y-6" data-id="element-9849" data-mp="1ertyov359cx2">
          {/* Djay intro */}
          <div className="flex items-start gap-3 p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl" data-id="element-9850" data-mp="3e3mzz3wyt4fl">
            <DjayNarrator size="xs" emotion="encouraging" data-id="element-9851" data-mp="395d40r3f1zsx" />
            <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9852" data-mp="3hlapgruiroky">
              Werk je samen met een collega aan deze klas? Geef hen de
              docentcode hieronder — dat is een <strong data-id="element-9853" data-mp="3ll333iyrskx6">andere</strong> code dan
              de klascode die je leerlingen gebruiken. Een co-docent kan
              leerlingen begeleiden en huiswerk uitzetten, maar alleen jij blijft
              eigenaar.
            </p>
          </div>

          {/* Invite code */}
          {isOwner && <div data-id="element-9854" data-mp="2yo9fstq0t0ao">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9855" data-mp="2yot20mmpeaui">
                Docentcode delen
              </h3>
              {canAddMore ? code ? <div className="p-4 bg-white border-2 border-dashed border-indigo-200 rounded-2xl" data-id="element-9856" data-mp="37lju6ny4x5jt">
                  <div className="flex items-center justify-between gap-3" data-id="element-9857" data-mp="3cnsnjlex69mg">
                    <div data-id="element-9858" data-mp="mvugie963psp">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-9859" data-mp="mwnpllvlmre0">
                        Docentcode
                      </p>
                      <p className="text-2xl font-mono font-black text-slate-900 tracking-widest" data-id="element-9860" data-mp="20s5lwj0do2m0">
                        {code}
                      </p>
                    </div>
                    <Button onClick={handleCopy} variant="secondary" size="sm" className="flex items-center gap-2" data-id="element-9861" data-mp="29u50z5ru0wmb">
                  
                      {copied ? <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-9862" data-mp="24wvenyzqp12p" />
                          Gekopieerd
                        </> : <>
                          <Copy className="w-4 h-4" data-id="element-9863" data-mp="2ernezakpi216" />
                          Kopieer
                        </>}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3 leading-relaxed" data-id="element-9864" data-mp="2oirqu1f356rs">
                    Je collega voert deze code in via{' '}
                    <span className="font-bold" data-id="element-9865" data-mp="32j2j98w1mtf2">
                      Klas Beheer → "Ik heb een docentcode"
                    </span>{' '}
                    in hun eigen docentportaal.
                  </p>
                  {/*
                     Vervangen is hier de enige manier om een code ongeldig te
                     maken. Dat staat expliciet in de knoptekst, want "nieuwe
                     code" leest als toevoegen terwijl het intrekken is.
                    */}
                  <button onClick={() => setPendingReplace(true)} className="mt-3 text-xs font-bold text-slate-500 underline hover:text-rose-600 transition-colors" data-id="element-9866" data-mp="3c53rcny5mnqp">
                    Code vervangen (de oude werkt daarna niet meer)
                  </button>
                </div> : <div className="p-4 bg-white border-2 border-dashed border-slate-200 rounded-2xl" data-id="element-9867" data-mp="2ogc7qkymrdpj">
                  <p className="text-sm font-bold text-slate-900 mb-1" data-id="element-9868" data-mp="2tff29bgzi1j9">
                    Nog geen docentcode
                  </p>
                  {/*
                     Bewust niet automatisch aangemaakt bij het maken van een
                     klas: een code die altijd bestaat, is ook altijd in omloop.
                     Hij ontstaat op het moment dat de eigenaar iemand wil
                     uitnodigen.
                    */}
                  <p className="text-xs text-slate-500 leading-relaxed mb-3" data-id="element-9869" data-mp="l70fvcm7qdr3">
                    Zolang er geen docentcode is, kan niemand co-docent worden.
                    Maak er een aan wanneer je een collega wilt uitnodigen.
                  </p>
                  <Button onClick={handleCreateOrReplace} size="sm" data-id="element-9870" data-mp="18x4hxfjr65fz">
                    Docentcode aanmaken
                  </Button>
                </div> : <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl" data-id="element-9871" data-mp="13zl2icbbjmmg">
                  <p className="text-sm font-bold text-amber-900" data-id="element-9872" data-mp="yy5j0oosho66">
                    Maximum bereikt
                  </p>
                  <p className="text-xs text-amber-800 mt-1" data-id="element-9873" data-mp="ux26oc5yaa9p">
                    Deze klas heeft al {MAX_CO_TEACHERS_PER_CLASSROOM}{' '}
                    co-docenten. Verwijder eerst een co-docent om iemand anders
                    toe te voegen.
                  </p>
                </div>}
            </div>}

          {/*
           Deze docentcode is de TIJDELIJKE vorm. De eindvorm is een
           uitnodiging per e-mailadres (`coteacher_invites`, migratie 0003):
           eenmalig, 7 dagen geldig, gebonden aan één persoon en in te trekken.
           Die functies staan serverzijdig klaar maar zijn niet aan te sluiten
           zolang klassen alleen lokaal bestaan — er is geen klas-id op de
           server om een uitnodiging aan te hangen.
            Het verschil staat er expliciet bij, en dat is geen slag om de arm:
           een code die niet verloopt en niet aan een persoon hangt, blijft
           geldig zolang niemand hem vervangt. Wie dat niet weet, deelt hem één
           keer en denkt daarna dat het klaar is.
           */}
          {isOwner && <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-id="element-9874" data-mp="sjzz323x8uxz">
              <div className="flex items-center gap-2 mb-1" data-id="element-9875" data-mp="xhhz22zynztu">
                <Mail className="w-4 h-4 text-slate-400" data-id="element-9876" data-mp="xk47s5yl8ldn" />
                <p className="text-sm font-bold text-slate-700" data-id="element-9877" data-mp="2zeigwsf28ccr">
                  Straks: uitnodigen per e-mailadres
                </p>
              </div>
              <p className="text-xs leading-relaxed text-slate-500" data-id="element-9878" data-mp="395dayiyjsg8r">
                Een uitnodiging op naam vervalt vanzelf na 7 dagen en werkt maar
                één keer. Tot die tijd geldt deze docentcode{' '}
                <strong className="font-bold text-slate-700" data-id="element-9879" data-mp="2gg650ywrkodh">
                  zolang je hem niet vervangt
                </strong>
                . Geef hem dus één op één door, en vervang hem als een collega
                vertrekt.
              </p>
            </div>}

          {/* Current teachers */}
          <div data-id="element-9880" data-mp="2lfj0u95m55s7">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9881" data-mp="2lf0wep8srl97">
              Huidige docenten
            </h3>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-9882" data-mp="5qhdeix1w2u9">
              {/* Owner */}
              <li className="flex items-center justify-between px-4 py-3" data-id="element-9883" data-mp="1imwyoolflrmg">
                <div className="flex items-center gap-3 min-w-0" data-id="element-9884" data-mp="1nm1azo3nk5h0">
                  <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-9885" data-mp="1nk6m5yp9ft7n">
                    {(teacherById[classroom.teacherId]?.displayName ?? '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0" data-id="element-9886" data-mp="22cgtuw8lj1c0">
                    <p className="text-sm font-bold text-slate-900 truncate" data-id="element-9887" data-mp="26crcp9hhlm8a">
                      {teacherById[classroom.teacherId]?.displayName ?? 'Onbekend'}
                      {classroom.teacherId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-9888" data-mp="1xk7a5xy2qxi6">
                          (jij)
                        </span>}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded" data-id="element-9889" data-mp="2q19w1afta5f2">
                  Eigenaar
                </span>
              </li>

              {/* Co-teachers */}
              {coTeachers.length === 0 ? <li className="px-4 py-6 text-center" data-id="element-9890" data-mp="uzarx2l03tx4">
                  <div className="flex justify-center mb-2" data-id="element-9891" data-mp="h0kmmzo12nxo">
                    <Users className="w-5 h-5 text-slate-300" data-id="element-9892" data-mp="l37gf649eixi" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium" data-id="element-9893" data-mp="l5260xqh6z7x">
                    Nog geen co-docenten
                  </p>
                </li> : coTeachers.map(coId => {
              const co = teacherById[coId];
              const name = co?.displayName ?? 'Onbekende docent';
              return <li key={coId} className="flex items-center justify-between px-4 py-3" data-id="element-9894" data-mp="3th6c06nbk2ah">
                    
                      <div className="flex items-center gap-3 min-w-0" data-id="element-9895" data-mp="3kiciip5abs15">
                        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-9896" data-mp="3kj4cxwn5bjpu">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0" data-id="element-9897" data-mp="30x2kcsbfj38f">
                          <p className="text-sm font-bold text-slate-900 truncate" data-id="element-9898" data-mp="30ydm6mlbe0ww">
                            {name}
                            {coId === activeAccount.id && <span className="ml-2 text-xs font-medium text-slate-400" data-id="element-9899" data-mp="3au78uysqzase">
                                (jij)
                              </span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-9900" data-mp="3jv54ott18bzw">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-100 text-amber-700 rounded" data-id="element-9901" data-mp="2dlxxcgk27mn">
                          Co-docent
                        </span>
                        {isOwner && onRequestTransfer && <button onClick={() => setTransferTarget(coId)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Eigenaarschap overdragen aan deze co-docent" data-id="element-9902" data-mp="bd8xrttmz5dl">
                        
                            <ArrowRightLeft className="w-4 h-4" data-id="element-9903" data-mp="358cyjtffthvc" />
                          </button>}
                      </div>
                    </li>;
            })}
            </ul>
          </div>
        </div>
      </SlideOver>

      {pendingReplace && <ConfirmDialog open={true} title="Docentcode vervangen?" message="De huidige docentcode werkt daarna niet meer. Heb je hem al aan een collega gegeven die nog niet is toegetreden, dan moet je de nieuwe code opnieuw doorgeven. Co-docenten die al zijn toegevoegd, houden hun toegang." confirmLabel="Ja, vervang de code" cancelLabel="Annuleren" variant="danger" onConfirm={handleCreateOrReplace} onCancel={() => setPendingReplace(false)} data-id="element-9904" data-mp="3e1q2dqf1i3w7" />}

      {transferTarget && onRequestTransfer && <ConfirmDialog open={true} title="Eigenaarschap overdragen?" message={`Hierna is ${teacherById[transferTarget]?.displayName ?? 'deze docent'} de nieuwe eigenaar van ${classroom.name}. Jij blijft betrokken als co-docent. Deze actie is niet ongedaan te maken.`} confirmLabel="Ja, draag over" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      onRequestTransfer(transferTarget);
      setTransferTarget(null);
      onClose();
    }} onCancel={() => setTransferTarget(null)} data-id="element-9905" data-mp="1yl7p5z0vfdzc" />}
    </>;
}